// @ts-nocheck
import React, { useEffect, useState } from "react";
import {
  Center,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Alert,
  AlertDescription,
  useToast,
  Flex,
  Img,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import AlertComponent from "components/Alert";
import qs from "qs";
import {
  useSetPasswordMutation,
  useSubmitNewPasswordMutation,
  useRecoveryCodeValidationMutation,
} from "redux/services/identity";
import LoadingOverlay from "components/LoadingOverlay";
import * as yup from "yup";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { VALIDATIONS } from "utils/validations";
import { selectAuth } from "redux/slices/authSlice";
import { useTypedSelector } from "redux/hooks";
import env from "env";

const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .matches(VALIDATIONS.PASSWORD.regex, VALIDATIONS.PASSWORD.message)
    .min(8, "Password must be between 8 and 12 characters in length.")
    .max(12, "Password must be between 8 and 12 characters in length.")
    .required("New Password is required"),
  confirmPassword: yup
    .string()
    .oneOf(
      [yup.ref("password")],
      "The new password and confirm new password fields doesn’t match"
    )
    .required("Confirm Password is required"),
});

const RenderExpiredLinkContent = () => (
  <Flex direction="column" alignItems="center" gap={"20px"}>
    <Img src="/assets/icons/time-out.png" alt="Time Out" h={"90px"} />
    <Text
      data-testid="screen-title"
      textAlign="center"
      textStyle="h1"
      mb={"30px"}
      color={"red.500"}
    >
      The link is expired or invalid.
    </Text>
  </Flex>
);

const getTitle = (isCreatePassword: boolean) => {
  return isCreatePassword ? "Create Password" : "Reset Password";
};

const getPasswordLabel = (isCreatePassword: boolean) => {
  return isCreatePassword ? "Password" : "New Password";
};

const getConfirmPasswordLabel = (isCreatePassword: boolean) => {
  return isCreatePassword ? "Confirm Password" : "Confirm New Password";
};

const SetPasswordAccount: React.FC = () => {
  const toast = useToast();
  const { search } = useLocation();
  const [isCheckingLinkExpired, setIsCheckingLinkExpired] = useState(false);
  const [isLinkExpried, setIsLinkExpired] = useState(false);

  const [checkingLinkExpiredMutation] = useRecoveryCodeValidationMutation();
  const [
    submitNewPasswordMutation,
    {
      isSuccess: isSuccessSubmitNewPassword,
      error: submitNewPasswordError,
      isLoading: isLoadingSubmitNewPassword,
    },
  ] = useSubmitNewPasswordMutation();
  const [
    setPasswordMutation,
    {
      isSuccess: isSuccessSetPassword,
      error: setPasswordError,
      isLoading: isLoadingSetPassword,
    },
  ] = useSetPasswordMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const {
    otp: verifyCode,
    passwordToken,
    scenario,
  }: any = qs.parse(search.replace("?", ""));

  const isCreatePassword = scenario === "set-password";


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    try {
      await passwordSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((error) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (isValid) {
      if (passwordToken) {
        setPasswordMutation({
          passwordToken,
          password: formData.password,
        });
        return;
      }

      if (verifyCode) {
        submitNewPasswordMutation({
          verifyCode: passwordToken || verifyCode,
          password: formData.password,
        });
      }
    }
  };
  const { accessToken } = useTypedSelector(selectAuth);

  useEffect(() => {
    if (isSuccessSetPassword || isSuccessSubmitNewPassword) {
      toast({
        title: isCreatePassword
          ? "Password created successfully."
          : "Password reset successfully.",
        description: "You will be redirected to the Login screen shortly.",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top-right",
      });
      const timeoutRedirect = setTimeout(() => {
        const isLoggedIn = !!accessToken;
       
        if (isLoggedIn) {
          window.location.href = env.api.baseUrl.logoutUrl;
        } else {
          window.location.href = `${env.app.rootUrl}`;
        }
        clearTimeout(timeoutRedirect);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSetPassword, isSuccessSubmitNewPassword]);

  useEffect(() => {
    const checkingLinkExpired = async () => {
      if (!passwordToken && verifyCode) {
        try {
          setIsCheckingLinkExpired(true);
          const data = await checkingLinkExpiredMutation({
            recoveryCode: verifyCode,
          });
          if (data?.error) {
            setIsLinkExpired(true);
          }
        } catch (e) {
          console.log("error", e);
          setIsLinkExpired(true);
        } finally {
          setIsCheckingLinkExpired(false);
        }
      }
    };
    checkingLinkExpired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    isLoadingSetPassword ||
    isLoadingSubmitNewPassword ||
    isCheckingLinkExpired
  ) {
    return <LoadingOverlay h="100%" />;
  }

  if (isLinkExpried) {
    return <RenderExpiredLinkContent />;
  }

  return (
    <Center
      className="SetPassword"
      w="100%"
      h="100%"
      overflow="auto"
      mt="0 !important"
      p={["24px 15px", null, 0]}
    >
      <form onSubmit={handleSubmit} style={{ width: "384px" }}>
        <Text
          data-testid="screen-title"
          textTransform={"uppercase"}
          textAlign="center"
          textStyle="h1"
          mb={"30px"}
        >
          {getTitle(isCreatePassword)}
        </Text>

        {(setPasswordError || submitNewPasswordError) && (
          <AlertComponent
            data-testid="alert-error"
            status="error"
            mb="10px"
            title={
              (
                (setPasswordError || submitNewPasswordError) as any
              )?.data?.errors
                ?.map((err: any) => err.message)
                ?.join(", ") || "Something went wrong!"
            }
          />
        )}

        <FormControl isInvalid={errors.password} mb="4">
          <FormLabel>{getPasswordLabel(isCreatePassword)}</FormLabel>
          <InputGroup>
            <Input
              data-testid="password-input"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              // maxLength={12}
            />
            <InputRightElement>
              <IconButton
                data-testid="view-password"
                variant="unstyled"
                onClick={() => setShowPassword(!showPassword)}
                icon={!showPassword ? <ViewOffIcon /> : <ViewIcon />}
                aria-label="Toggle Password Visibility"
              />
            </InputRightElement>
          </InputGroup>
          {errors.password && (
            <FormErrorMessage fontSize={12}>{errors.password}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={errors.confirmPassword} mb="4">
          <FormLabel>{getConfirmPasswordLabel(isCreatePassword)}</FormLabel>
          <InputGroup>
            <Input
              data-testid="confirm-password-input"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              // maxLength={12}
            />
            <InputRightElement>
              <IconButton
                data-testid="view-confirm-password"
                variant="unstyled"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                icon={!showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                aria-label="Toggle Confirm Password Visibility"
              />
            </InputRightElement>
          </InputGroup>
          {errors.confirmPassword && (
            <FormErrorMessage fontSize={12}>
              {errors.confirmPassword}
            </FormErrorMessage>
          )}
        </FormControl>

        <Button
          data-testid="submit-password"
          disabled={isLoadingSetPassword || isLoadingSubmitNewPassword}
          type="submit"
          variant="solid"
          colorScheme="blue"
          width="full"
        >
          {getTitle(isCreatePassword)}
        </Button>

        <Alert
          mt="12px"
          backgroundColor="gray.100"
          variant="solid"
          borderRadius="10px"
          alignItems="flex-start"
        >
          <AlertDescription color="black">
            • Password must be between 8 and 12 characters in length.
            <br />• Password must include a combination of (a-z), (A-Z), (0-9),
            (!@#$%&*).
            {/* <br />• Previous passwords are not acceptable. */}
          </AlertDescription>
        </Alert>
      </form>
    </Center>
  );
};

export default SetPasswordAccount;
